import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Youtube, Video, Link2, Radio, Square, Copy, Clapperboard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const field = "w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#144F75]";

function Secret({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="text-xs">
      <p className="text-[#8FA89B] mb-1">{label}</p>
      <div className="flex gap-2">
        <code className="flex-1 truncate rounded-lg bg-[#F5F1EA] px-2 py-1.5" data-testid={testid}>{value}</code>
        <button type="button" onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} aria-label={`Copy ${label}`} className="text-[#144F75]"><Copy size={14} /></button>
      </div>
    </div>
  );
}

export const StreamHostPanel = ({ eid, s, refresh }) => {
  const h = s.host || {};
  const [link, setLink] = useState(h.watch_url || "");
  const [replay, setReplay] = useState(h.replay_url || "");
  const [busy, setBusy] = useState("");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(""); }
  };
  const put = (body) => api.put(`/events/${eid}/stream`, body);
  return (
    <div className="eyw-card p-5 space-y-4" data-testid="stream-host-panel">
      <div>
        <h2 className="font-serif text-xl text-[#1A242B]">Host controls</h2>
        <p className="text-xs text-[#8FA89B]">Only hosts see this panel.</p>
      </div>
      <Link to={`/events/${eid}/studio`} data-testid="stream-open-studio" className="eyw-btn w-full justify-center !py-2 text-sm"><Clapperboard size={15} /> Open broadcast studio</Link>
      <div className="grid grid-cols-2 gap-2">
        <button disabled={!!busy} onClick={() => run("yt", () => api.post(`/events/${eid}/stream/youtube`), "YouTube Live broadcast created")} data-testid="stream-link-youtube" className="eyw-btn-outline !py-2 text-sm justify-center"><Youtube size={15} /> YouTube Live</button>
        <button disabled={!!busy} onClick={() => run("zoom", () => api.post(`/events/${eid}/stream/zoom`), "Zoom meeting linked")} data-testid="stream-link-zoom" className="eyw-btn-outline !py-2 text-sm justify-center"><Video size={15} /> Zoom</button>
      </div>
      <Secret label="RTMP server (for OBS)" value={h.ingestion_url} testid="stream-ingest-url" />
      <Secret label="Stream key — keep private" value={h.stream_name} testid="stream-key" />
      {h.start_url && <a href={h.start_url} target="_blank" rel="noreferrer" data-testid="stream-zoom-start" className="eyw-btn !py-2 text-sm w-full justify-center">Start Zoom as host</a>}
      <div className="space-y-2">
        <label className="text-xs text-[#4A5D6B]" htmlFor="stream-link">Or paste a stream link</label>
        <div className="flex gap-2">
          <input id="stream-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" data-testid="stream-link-input" className={field} />
          <button disabled={!!busy} onClick={() => run("link", () => put({ provider: "link", watch_url: link }), "Stream link saved")} data-testid="stream-link-save" className="eyw-btn !py-2 !px-3 text-sm"><Link2 size={14} /></button>
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-xs text-[#4A5D6B]" htmlFor="stream-access">Who can watch</label>
        <select id="stream-access" value={s.watch_access} onChange={(e) => run("acc", () => put({ watch_access: e.target.value }), "Viewing access updated")} data-testid="stream-access-select" className={field}>
          <option value="ticket_or_members">Ticket holders (members too, if the event is free)</option>
          <option value="ticket_only">Ticket holders only</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button disabled={!!busy || s.status === "live"} onClick={() => run("live", () => api.post(`/events/${eid}/stream/status`, { status: "live" }), "You're live")} data-testid="stream-go-live" className="eyw-btn !py-2 text-sm justify-center !bg-red-600"><Radio size={14} /> Go live</button>
        <button disabled={!!busy || s.status === "ended"} onClick={() => window.confirm("End the stream for everyone?") && run("end", () => api.post(`/events/${eid}/stream/status`, { status: "ended" }), "Stream ended")} data-testid="stream-end" className="eyw-btn-outline !py-2 text-sm justify-center"><Square size={14} /> End</button>
      </div>
      <div className="space-y-2">
        <label className="text-xs text-[#4A5D6B]" htmlFor="stream-replay">Replay link (YouTube streams use the broadcast automatically)</label>
        <div className="flex gap-2">
          <input id="stream-replay" value={replay} onChange={(e) => setReplay(e.target.value)} placeholder="https://" data-testid="stream-replay-input" className={field} />
          <button disabled={!!busy} onClick={() => run("rep", () => put({ replay_url: replay }), "Replay saved")} data-testid="stream-replay-save" className="eyw-btn !py-2 !px-3 text-sm">Save</button>
        </div>
        <p className="text-xs text-[#8FA89B]" data-testid="stream-replay-expiry">Replays stay open until {fmtDateTime(s.replay_expires_at)}.</p>
      </div>
    </div>
  );
};
